// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers. The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { ButtonIcon } from "@insite/mobius/Button";
import ButtonPresentationPropsDefault from "@insite/mobius/Button/presentationProps";
import { IconWrapper } from "@insite/mobius/Icon";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import get from "@insite/mobius/utilities/get";
import buttonSizeVariants from "@synd/MobiusOverrides/Button/buttonSizeVariants";
import { fontConfig } from "@synd/XDL/PreStyle/typography";
import { xBaseTheme } from "@synd/XDL/types";
import { getSystemColor } from "@synd/XDL/Utilities";
import { css } from "styled-components";

export const buttonTransparent = css`
    &,
    &:focus:not([disabled]),
    &:active:not([disabled]),
    &:disabled,
    &:focus:hover,
    &:hover:not(:disabled) {
        border: 0;
        background-color: transparent;
        box-shadow: none;
        outline: 0;
        transform: translate(0, 0);
        min-height: initial;
    }
`;

// Button color configurations
const buttonColors: { [key: string]: { [key: string]: string } } = {
    primary: {
        main: "common.ui9",
        hover: "common.ui10",
        focus: "common.ui11",
    },
    secondary: {
        main: "common.ui9",
        hover: "common.ui9",
        focus: "common.ui11",
    },
    tertiary: {
        main: "primary",
        hover: "common.brand03",
        focus: "common.ui3",
    },
    link: {
        main: "primary",
        hover: "common.ui12",
        focus: "common.ui13",
    },
};
const getSizeRelatedStyles = (size: keyof typeof buttonSizeVariants) => {
    return css`
        min-height: ${get(buttonSizeVariants, [size, "height"])}px;
        line-height: ${get(buttonSizeVariants, [size, "lineHeight"])};
        padding: ${get(buttonSizeVariants, [size, "padding"])};
    `;
};

const sharedButtonStyles = css`
    display: flex;
    align-items: center;
    justify-content: center;
    ${({ _sizeVariant }: any) => getSizeRelatedStyles(_sizeVariant)}

    ${ButtonIcon} {
        top: 0;
    }

    &.small {
        ${getSizeRelatedStyles("small")}
    }
    &.medium {
        ${getSizeRelatedStyles("medium")}
    }
    &.large {
        ${getSizeRelatedStyles("large")}
    }
`;

export default {
    primary: {
        ...ButtonPresentationPropsDefault,
        color: buttonColors.primary.main,
        buttonType: "solid",
        shape: "pill",
        mergeCss: true,
        hoverStyle: {
            background: `${getSystemColor(buttonColors.primary.hover)}`,
            borderColor: `${getSystemColor(buttonColors.primary.hover)}`,
            color: `${getSystemColor("common.white")}`,
        },
        typographyProps: {
            color: "common.white",
        },
        css: css`
            border-width: 1px;
            &:focus {
                background: ${getSystemColor(buttonColors.primary.focus)};
                border-color: ${getSystemColor(buttonColors.primary.focus)};
                border-color: ${getSystemColor(buttonColors.primary.focus)};
            }
            &:disabled {
                background: ${getSystemColor(buttonColors.primary.main, 0.33)};
                border-color: transparent;
            }
            ${IconWrapper} {
                color: ${getSystemColor("common.white")};
            }
            ${sharedButtonStyles}
        `,
    },
    secondary: {
        ...ButtonPresentationPropsDefault,
        color: buttonColors.secondary.main,
        buttonType: "outline",
        shape: "pill",
        mergeCss: true,
        hoverStyle: {
            background: `${getSystemColor(buttonColors.secondary.hover)}`,
            borderColor: `${getSystemColor(buttonColors.secondary.hover)}`,
            color: `${getSystemColor("common.white")}`,
        },

        css: css`
            border-width: 1px;
            &:focus {
                border-color: ${getSystemColor(buttonColors.secondary.focus)};
                color: ${getSystemColor(buttonColors.secondary.focus)};
            }
            &:disabled {
                border-color: ${getSystemColor(buttonColors.secondary.main, 0.33)};
                color: ${getSystemColor(buttonColors.secondary.main, 0.33)};
            }
            ${sharedButtonStyles}
        `,
    },
    tertiary: {
        ...ButtonPresentationPropsDefault,
        color: buttonColors.tertiary.main,
        buttonType: "solid",
        shape: "pill",
        mergeCss: true,
        hoverStyle: {
            background: `${getSystemColor(buttonColors.tertiary.hover)}`,
            borderColor: `${getSystemColor(buttonColors.tertiary.hover)}`,
            color: `${getSystemColor("common.white")}`,
        },
        typographyProps: {
            color: "common.white",
        },
        css: css`
            border-width: 1px;
            &:focus {
                background: ${getSystemColor(buttonColors.tertiary.focus)};
                border-color: ${getSystemColor(buttonColors.tertiary.focus)};
                border-color: ${getSystemColor(buttonColors.tertiary.focus)};
            }
            &:disabled {
                background: ${getSystemColor(buttonColors.tertiary.main, 0.33)};
                border-color: transparent;
            }
            ${IconWrapper} {
                color: ${getSystemColor("common.white")};
            }
            ${sharedButtonStyles}
        `,
    },
    link: {
        ...ButtonPresentationPropsDefault,
        color: buttonColors.link.main,
        buttonType: "outline",
        mergeCss: true,
        css: css`
            ${buttonTransparent}
            padding: 0;
            ${TypographyStyle} {
                font-weight: 500;
            }
            &&&:not(:disabled) {
                &,
                & ${IconWrapper} {
                    color: ${getSystemColor(buttonColors.link.main)};
                }
                &:hover {
                    &,
                    & ${IconWrapper} {
                        color: ${getSystemColor(buttonColors.link.hover)};
                    }
                }
                &:focus {
                    &,
                    & ${IconWrapper} {
                        color: ${getSystemColor(buttonColors.link.focus)};
                    }
                }
            }

            &&&:disabled {
                opacity: 0.33;
            }
        `,
    },
} as xBaseTheme["button"];
